import { ChevronDown, ChevronUp, Link2, Menu } from "lucide-react";
import type { Ref } from "react";

import { BrandLogo } from "@/brand/BrandLogo";
import { ThemeSwitcher } from "@/brand/ThemeSwitcher";
import { DriveChip } from "@/components/drive/DriveChip";
import { AccountChip } from "@/components/shell/AccountChip";
import { CommandPalette, type AtlasSearchHandle } from "@/components/shell/CommandPalette";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { useIsCompact } from "@/hooks/use-media-query";
import { useShell } from "@/stores/shell";

/**
 * The compact (phone / portrait-tablet / touch-landscape-tablet) top bar.
 * Collapsed by default to a slim pill — the same idiom as the desktop
 * `appshell/TopBar` — so the workspace and its single Dockview tab strip own
 * the vertical space; a small `⌄` expands it to hold search / add / account.
 * The notch inset (`pt-safe`) is honoured in BOTH states so nothing hides under
 * system chrome. State lives in `stores/shell` (`compactTopOpen`, persisted).
 */
export function TopBar({
  onAddWebsite,
  onSmartExport,
  onManageTokens,
  onManageBackups,
  searchRef,
}: {
  onAddWebsite: () => void;
  onSmartExport: () => void;
  onManageTokens: () => void;
  onManageBackups: () => void;
  /** App holds this handle so ⌘K / the LeftBar can open the results on
   * tablet/compact. */
  searchRef?: Ref<AtlasSearchHandle>;
}) {
  // The single-pane shell (incl. landscape tablets) opens files via a drawer, so
  // the hamburger shows whenever the layout is compact — matched to the JS
  // breakpoint, not a fixed CSS one, so it never disagrees with the dock.
  const compact = useIsCompact();
  const open = useShell((s) => s.compactTopOpen);

  if (!open) {
    return (
      <header className="hairline-b z-30 flex shrink-0 flex-col bg-background pt-safe">
        <div className="flex h-5 items-center justify-center">
          <Tip label="Show top bar" side="bottom">
            <button
              type="button"
              onClick={() => useShell.getState().toggleCompactTop()}
              aria-label="Show top bar"
              className="grid h-5 w-12 place-items-center rounded-b-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </button>
          </Tip>
        </div>
      </header>
    );
  }

  return (
    <header className="hairline-b z-30 flex h-[calc(52px+env(safe-area-inset-top,0px))] shrink-0 items-center gap-2 px-3 pt-safe sm:gap-4 sm:px-5">
      {/* Mobile/tablet: hamburger opens the files drawer. */}
      {compact && (
        <button
          onClick={() => useShell.getState().toggleLeft()}
          aria-label="Open files"
          className="-ml-1 grid h-9 w-9 shrink-0 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      <a
        href="https://graphview.org"
        aria-label="GraphView home"
        className="hidden shrink-0 rounded-sm transition-opacity hover:opacity-80 sm:block"
      >
        <BrandLogo className="h-[30px] overflow-hidden rounded-sm" />
      </a>
      <span className="sr-only">GraphView</span>

      <CommandPalette
        controllerRef={searchRef}
        barClassName="mx-auto h-8 w-full max-w-[420px] bg-card sm:w-[min(420px,38vw)]"
        placeholder="Search the atlas…"
        onAddWebsite={onAddWebsite}
        onSmartExport={onSmartExport}
        onManageTokens={onManageTokens}
        onManageBackups={onManageBackups}
      />

      <div className="flex items-center gap-1.5">
        <Tip label="Add website">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Add website"
            onClick={onAddWebsite}
            className="hidden sm:inline-flex"
          >
            <Link2 className="h-4 w-4" />
          </Button>
        </Tip>
        <ThemeSwitcher className="hidden sm:block" />
        <span className="hidden sm:block">
          <DriveChip />
        </span>
        <AccountChip />
        <Tip label="Collapse top bar" side="bottom">
          <button
            type="button"
            onClick={() => useShell.getState().toggleCompactTop()}
            aria-label="Collapse top bar"
            className="grid h-8 w-8 shrink-0 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronUp className="h-4 w-4" aria-hidden />
          </button>
        </Tip>
      </div>
    </header>
  );
}
